import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  IndexTable,
  Text,
  Filters,
  ChoiceList,
  Pagination,
  EmptyState,
  Spinner,
  InlineStack,
  Box,
} from '@shopify/polaris';
import { api } from '../utils/api';
import { getStatusBadge, formatDate, getCarrierName } from '../utils/helpers';

interface Shipment {
  id: string;
  trackingNumber: string;
  carrier: string;
  status: string;
  statusDetail?: string;
  orderNumber?: string;
  customerName?: string;
  createdAt: string;
}

interface ShipmentsResponse {
  shipments: Shipment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export default function Shipments() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [queryValue, setQueryValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [carrierFilter, setCarrierFilter] = useState<string[]>([]);

  const page = parseInt(searchParams.get('page') ?? '1');

  const { data, isLoading } = useQuery<ShipmentsResponse>({
    queryKey: ['shipments', page, statusFilter, carrierFilter, queryValue],
    queryFn: () =>
      api.get('/shipments', {
        page,
        limit: 20,
        status: statusFilter.join(',') ?? undefined,
        carrier: carrierFilter.join(',') ?? undefined,
        search: queryValue ?? undefined,
      }),
  });

  const shipments = data?.shipments ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0, pages: 1 };

  const handleQueryChange = useCallback((value: string) => {
    setQueryValue(value);
  }, []);

  const handleStatusChange = useCallback((value: string[]) => {
    setStatusFilter(value);
    setSearchParams({ page: '1' });
  }, [setSearchParams]);

  const handleCarrierChange = useCallback((value: string[]) => {
    setCarrierFilter(value);
    setSearchParams({ page: '1' });
  }, [setSearchParams]);

  const handleClearAll = useCallback(() => {
    setQueryValue('');
    setStatusFilter([]);
    setCarrierFilter([]);
  }, []);

  const filters = [
    {
      key: 'status',
      label: 'Statut',
      filter: (
        <ChoiceList
          title="Statut"
          titleHidden
          choices={[
            { label: 'En attente', value: 'pending' },
            { label: 'Pris en charge', value: 'picked_up' },
            { label: 'En transit', value: 'in_transit' },
            { label: 'En livraison', value: 'out_for_delivery' },
            { label: 'Livré', value: 'delivered' },
            { label: 'Exception', value: 'exception' },
          ]}
          selected={statusFilter}
          onChange={handleStatusChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'carrier',
      label: 'Transporteur',
      filter: (
        <ChoiceList
          title="Transporteur"
          titleHidden
          choices={[
            { label: 'Colissimo', value: 'colissimo' },
            { label: 'Chronopost', value: 'chronopost' },
            { label: 'DHL', value: 'dhl' },
            { label: 'UPS', value: 'ups' },
            { label: 'FedEx', value: 'fedex' },
            { label: 'Mondial Relay', value: 'mondial_relay' },
          ]}
          selected={carrierFilter}
          onChange={handleCarrierChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = [
    ...statusFilter.map((status) => ({
      key: 'status-' + status,
      label: 'Statut: ' + status,
      onRemove: () => setStatusFilter(statusFilter.filter((s) => s !== status)),
    })),
    ...carrierFilter.map((carrier) => ({
      key: 'carrier-' + carrier,
      label: 'Transporteur: ' + getCarrierName(carrier),
      onRemove: () => setCarrierFilter(carrierFilter.filter((c) => c !== carrier)),
    })),
  ];

  const rowMarkup = shipments.map((shipment, index) => (
    <IndexTable.Row
      id={shipment.id}
      key={shipment.id}
      position={index}
      onClick={() => navigate('/shipments/' + shipment.id)}
    >
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {shipment.trackingNumber}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{getCarrierName(shipment.carrier)}</IndexTable.Cell>
      <IndexTable.Cell>{getStatusBadge(shipment.status)}</IndexTable.Cell>
      <IndexTable.Cell>{shipment.orderNumber ?? '-'}</IndexTable.Cell>
      <IndexTable.Cell>{shipment.customerName ?? '-'}</IndexTable.Cell>
      <IndexTable.Cell>{formatDate(shipment.createdAt)}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  const emptyStateMarkup = (
    <EmptyState
      heading="Aucune expédition trouvée"
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
    >
      <p>Les expéditions apparaîtront ici automatiquement lors des fulfillments Shopify.</p>
    </EmptyState>
  );

  return (
    <Page title="Expéditions" fullWidth>
      <Card padding="0">
        <Filters
          queryValue={queryValue}
          filters={filters}
          appliedFilters={appliedFilters}
          onQueryChange={handleQueryChange}
          onQueryClear={() => setQueryValue('')}
          onClearAll={handleClearAll}
          queryPlaceholder="Rechercher par numéro de suivi ou commande..."
        />
        {isLoading ? (
          <Box padding="800">
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Box>
        ) : shipments.length === 0 ? (
          emptyStateMarkup
        ) : (
          <>
            <IndexTable
              itemCount={shipments.length}
              headings={[
                { title: 'Numéro de suivi' },
                { title: 'Transporteur' },
                { title: 'Statut' },
                { title: 'Commande' },
                { title: 'Client' },
                { title: 'Date' },
              ]}
              selectable={false}
            >
              {rowMarkup}
            </IndexTable>
            {pagination.pages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < pagination.pages}
                    onPrevious={() => setSearchParams({ page: String(page - 1) })}
                    onNext={() => setSearchParams({ page: String(page + 1) })}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>
    </Page>
  );
}
